/**
 * File: useNewVersionBarController.ts
 * Path: apps/admin/src/modules/layout/hooks/useNewVersionBarController.ts
 * Description: Keeps an open admin tab current. Asks /version.json which build is live when the tab opens,
 * when you come back to it, and every few minutes; once a newer build is out, the next page opened loads it.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchDeployedVersion, isNewerBuild } from "@/shared/utils/adminVersionUtils";

const CHECK_EVERY_MS = 5 * 60 * 1000;

export const useNewVersionBarController = (running: string | null, fetcher?: typeof fetch) => {
  const [stale, setStale] = useState(false);
  const { pathname, search } = useLocation();
  const firstPath = useRef(pathname);

  useEffect(() => {
    if (running === null) return;
    let live = true;
    const check = async () => {
      const deployed = await fetchDeployedVersion(fetcher);
      if (live && isNewerBuild(running, deployed)) setStale(true);
    };
    void check();
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      live = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [running, fetcher]);

  // Opening another page is a safe moment: load it fresh, from the new build.
  useEffect(() => {
    if (stale && pathname !== firstPath.current) window.location.assign(`${pathname}${search}`);
  }, [stale, pathname, search]);

  return { stale, reload: () => window.location.reload() };
};
